import get from 'lodash/get';
import allActions from '../actions/index';
import AppConstants from '../components/App/constants';
import {
  getAnswerSuccessErrorAndMessage,
  getExtras,
  getErrorMessage,
  trackRegistrationError,
  ERROR_MESSAGES,
  getInitializeSuccessResponse,
  getLoginSuccessResponse,
  isCreditWaitlistEnabledForFlow,
  setNextRegStepFromRole,
  setNextRegStepFromDob,
  checkPhoneSuccess
} from './helpers';

const {
  // Async requests
  CHECK_PHONE_LOADING,
  CHECK_PHONE_SUCCESS,
  CHECK_PHONE_FAILURE,
  ACCESS_REQUEST_LOADING,
  ACCESS_REQUEST_SUCCESS,
  ACCESS_REQUEST_FAILURE,
  CONFIRM_CODE_LOADING,
  CONFIRM_CODE_SUCCESS,
  CONFIRM_CODE_FAILURE,
  VOICE_PIN_LOADING,
  VOICE_PIN_SUCCESS,
  VOICE_PIN_FAILURE,
  CHECK_VALID_EMAIL_SUCCESS,
  LOGIN_LOADING,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  FORGOT_PASSWORD_LOADING,
  FORGOT_PASSWORD_SUCCESS,
  FORGOT_PASSWORD_FAILURE,
  RESET_PASSWORD_LOADING,
  RESET_PASSWORD_SUCCESS,
  RESET_PASSWORD_FAILURE,
  GET_BANK_NAME_LOADING,
  GET_BANK_NAME_SUCCESS,
  GET_BANK_NAME_FAILURE,
  GET_USER_LOADING,
  GET_USER_SUCCESS,
  GET_USER_FAILURE,
  QUERY_GET_USER_LOADING,
  QUERY_GET_USER_SUCCESS,
  QUERY_GET_USER_FAILURE,
  QUERY_GET_INITIAL_FUNDING_LOADING,
  QUERY_GET_INITIAL_FUNDING_SUCCESS,
  QUERY_GET_INITIAL_FUNDING_FAILURE,
  INITIALIZE_LOADING,
  INITIALIZE_SUCCESS,
  INITIALIZE_FAILURE,
  ANSWER_LOADING,
  ANSWER_SUCCESS,
  ANSWER_FAILURE,
  COMPLETE_LOADING,
  COMPLETE_SUCCESS,
  COMPLETE_FAILURE,
  RESOLVE_ERROR_LOADING,
  RESOLVE_ERROR_SUCCESS,
  RESOLVE_ERROR_FAILURE,
  SAVE_DEBIT_LOADING,
  SAVE_DEBIT_SUCCESS,
  SAVE_DEBIT_FAILURE,
  SAVE_MANUAL_ACH_LOADING,
  SAVE_MANUAL_ACH_SUCCESS,
  SAVE_MANUAL_ACH_FAILURE,
  VERIFY_DEBIT_NOW_LOADING,
  VERIFY_DEBIT_NOW_SUCCESS,
  VERIFY_DEBIT_NOW_FAILURE,
  GET_BRAINTREE_TOKEN_LOADING,
  GET_BRAINTREE_TOKEN_SUCCESS,
  GET_BRAINTREE_TOKEN_FAILURE,
  GET_PLAID_TOKEN_LOADING,
  GET_PLAID_TOKEN_SUCCESS,
  GET_PLAID_TOKEN_FAILURE,
  GET_FUNDING_ACCOUNTS_LOADING,
  GET_FUNDING_ACCOUNTS_SUCCESS,
  GET_FUNDING_ACCOUNTS_FAILURE,
  VERIFY_DEBIT_LATER,
  GET_ANSWERS_LOADING,
  GET_ANSWERS_SUCCESS,
  GET_ANSWERS_FAILURE,
  GET_MICRODEPOSITS_LOADING,
  GET_MICRODEPOSITS_SUCCESS,
  GET_MICRODEPOSITS_FAILURE,
  GET_PRICING_PLAN_LOADING,
  GET_PRICING_PLAN_SUCCESS,
  GET_PRICING_PLAN_FAILURE,
  VERIFY_PROMO_CODE_SUCCESS,
  VERIFY_PROMO_CODE_FAILURE,
  GET_PROVE_REDIRECT_URI_LOADING,
  GET_PROVE_REDIRECT_URI_SUCCESS,
  GET_PROVE_REDIRECT_URI_FAILURE,
  VALIDATE_PLACEHOLDER_TOKEN_SUCCESS,
  VALIDATE_PLACEHOLDER_TOKEN_FAILURE,
  EMAIL_VERIFICATION_SUCCESS,
  EMAIL_VERIFICATION_FAILURE,

  // Sync requests
  SET_DEVICE_UUID,
  CREATE_ERROR,
  CLEAR_ERROR,
  START_LOADING,
  STOP_LOADING,
  SET_NEXT_REG_STEP,
  SET_DOB,
  SET_ROLE,
  SET_HIDE_FUNDING,
  SET_PREREGISTERED_USER,
  COMPLETE_RESET_KBA_FLOW,
  SET_MINIMUM_WALLET_LOAD_AMOUNT,
  DISPLAY_LEGAL_ADDRESS,
  SET_SMS_NOTIFICIATIONS_OPTED_STATUS,
  SET_PLAID_OAUTH_MANUAL_OVERRIDE,
  SET_PLAID_OAUTH_ANDROID_LINK,
  SET_NATIVE_PLAID_FLOW_ENABLED,
  SET_TOGGLES_LOADED,
  SET_IS_FB_PIXEL_ENABLED,
  INITIALIZE_IN_APP,
  SET_PHONE_NUMBER_PROVIDED,
  SET_FIRST_NAME,
  SET_PARTNER,
  SET_PROMOTION_ID,
  SET_PARTNER_PARAMS,
  SET_SHORTCUT,
  SET_NO_PHONE_REG_ENTRY,
  SET_LINK_SOURCE,
  SET_EMAIL_BLUR_ENABLED,
  SET_IP_ADDRESS,
  SET_ACCESS_MANAGEMENT_ENABLED,
  SET_PROMO_CODE_VERIFICATION_ENABLED,
  SET_PLAN_CHANGES_TERMS_AND_CONDITIONS,
  SET_CREDIT_WAITLIST,
  SET_SMS_NOTIFICATIONS_ENABLED,
  SET_PREFER_NOT_TO_SAY_OPTION_ENABLED,
  SET_APPROVER_INVITE_PROPERTIES,
  SET_REQUIRE_TOKEN_FOR_APPROVERS_ENABLED,
  SET_PMA_FINAL_TARGET,
  SET_PROVE_MOBILE_AUTH_ENABLED,
  SET_PMA_STATE,
  SET_MOCK_PMA,
  INVALIDATE_PMA,
  SET_ENHANCED_DEBIT_OPTIONS,
  SET_FEATURE_TOGGLES,
  SET_NEW_DEACTIVATED_ERROR_MSG_ENABLED,
  SET_LAYOUT_TYPE
} = allActions;

export const INITIAL_STATE = {
  tempPhoneNumber: null,
  loading: false,
  submitPlaidLoading: false,
  emailCorrection: null,
  deviceToken: null,
  regAccessToken: null,
  nextRegStep: null,
  completeOnInit: false,
  preregisteredUser: null,
  initialLoadAmount: null,
  togglesDirty: true,
  role: null,
  dob: null,
  totalRegSteps: null,
  stepNumber: null,
  errorCode: null,
  error: null,
  achError: null,
  achMessage: null,
  message: null,
  registeredUser: null,
  pendingVerification: null,
  bankName: null,
  needsVerification: false,
  isParentInvitedByChild: false,
  isChildInvitedByParent: false,
  needsAccessRequest: false,
  questions: [],
  unresolvedErrors: [],
  registrationToken: null,
  registrationId: null,
  hideFunding: {
    hidePlaid: false,
    hideManualACH: false,
    hideDebit: false
  },
  enhancedDebit: {
    enhancedDebitEnabled: false,
    applePayEnabled: false,
    googlePayEnabled: false,
    threeDSEnabled: false,
    paypalEnabled: false,
    venmoEnabled: false
  },
  resetKBAFlow: false,
  walletMinimum: {
    shouldShift: false,
    amount: AppConstants.MINIMUM_WALLET_LOAD.defaultMinimumAmount || 20.0,
    displayAmount: AppConstants.MINIMUM_WALLET_LOAD.defaultDisplayMinimumAmount
  },
  plaidOAuthManualOverrideEnabled: false,
  plaidOAuthAndroidLinkEnabled: false,
  nativePlaidFlowEnabled: false,
  plaidLinkToken: '',
  currentPlan: 'standard',
  walletId: null,
  isFbPixelEnabled: false,
  appOs: null,
  appVersion: null,
  embeddedWebreg: false,
  inAppPreReg: false,
  prefilledAnswers: {},
  phoneNumberProvided: false,
  firstName: '',
  partner: '',
  ipAddress: '',
  promotionId: '',
  shortcut: '',
  shortcutPlan: '',
  accessManagementEnabled: false,
  promoCode: null,
  promoCodeVerificationEnabled: false,
  planChangesTermsAndConditions: false,
  proveMobileAuthEnabled: false,
  proveMobileAuthState: null,
  noPhoneNumberPriceText: false,
  preferNotToSayOptionEnabled: false,
  proveRedirectUri: null,
  placeholderToken: null,
  shouldBounceInvitedUsersWhoDidntUseInviteLink: false,
  requireTokenForApproversEnabled: false,
  emailVerificationStatus: 'unverified',
  mockPma: {
    enabled: false,
    state: null,
    phone: null
  },
  featureToggles: {},
  layoutType: 'default',
  showPlanFlowReturningUserMessage: false,
  smsNotificationsOptedStatus: null
};

export default function (state = INITIAL_STATE, action) {
  let loginErrorMessage = '';
  let answerFailureMessage = '';
  let saveDebitFailureMessage = '';
  let saveACHFailureMessage = '';
  let setFundingAccountExtras;
  let createUserExtras;
  let inAppPreReg = false;
  let nextRegStep;

  // eslint-disable-next-line default-case
  switch (action.type) {
    case INITIALIZE_SUCCESS:
      return {
        ...state,
        ...getInitializeSuccessResponse(action, state)
      };

    case INITIALIZE_LOADING:
      return {
        ...state,
        loading: true,
        initializeLoading: true,
        error: false
      };

    case INITIALIZE_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'widget_page',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        initializeLoading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.status : '',
        regAccessToken:
          action?.error?.response?.status === 401 ? null : state.regAccessToken,
        nextRegStep:
          action?.error?.response?.status === 401 ? 'phone' : state.nextRegStep,
        message: getErrorMessage(action)
      };

    case ANSWER_LOADING:
      return {
        ...state,
        loading: true,
        answerLoading: true,
        message: '',
        error: false
      };

    case COMPLETE_LOADING:
    case SAVE_DEBIT_LOADING:
    case SAVE_MANUAL_ACH_LOADING:
    case VERIFY_DEBIT_NOW_LOADING:
    case RESOLVE_ERROR_LOADING:
      return {
        ...state,
        loading: true,
        message: '',
        error: false
      };
    case VERIFY_PROMO_CODE_SUCCESS:
      return {
        ...state,
        promoCode: action.payload.data
      };
    case VERIFY_PROMO_CODE_FAILURE:
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: getErrorMessage(action)
      };
    case ANSWER_SUCCESS:
      setFundingAccountExtras =
        getExtras(action, 'set_plaid_bank_account') ||
        getExtras(action, 'set_manual_bank_account');
      createUserExtras = getExtras(action, 'create_user');

      return {
        ...state,
        ...getAnswerSuccessErrorAndMessage(action),
        questions: action.payload.data.nextQuestions,
        completeUser: !!(
          action.payload.data.nextQuestions &&
          action.payload.data.nextQuestions.indexOf('complete') !== -1
        ),
        extras: action.payload.data.extras
          ? action.payload.data.extras[0]
          : null,
        stargatewayKey: createUserExtras
          ? createUserExtras.token
          : state.stargatewayKey,
        setFundingAccountExtras,
        funding:
          setFundingAccountExtras && setFundingAccountExtras.funding
            ? setFundingAccountExtras.funding
            : null,
        needsVerification: getExtras(action, 'set_plaid_bank_account')
          ? false
          : state.needsVerification,
        flowId: action.payload.data.flowId,
        creditWaitlistEnabled:
          state.creditWaitlistEnabled &&
          isCreditWaitlistEnabledForFlow(action.payload.data.flowId),
        groupId: action.payload.data.groupId,
        totalGroupsInFlow: action.payload.data.totalGroupsInFlow,
        loading: false,
        registeredUser: {
          ...state.registeredUser,
          token: get(createUserExtras, 'token')
            ? get(createUserExtras, 'token')
            : get(state.registeredUser, 'token'),
          user:
            get(createUserExtras, 'user') && state.inAppPreReg
              ? get(createUserExtras, 'user')
              : get(state.registeredUser, 'user')
        },
        submitPlaidLoading: false,
        answerLoading: false,
        unresolvedErrors:
          action.payload.data.errors ||
          action.payload.data.unresolvedErrors ||
          [],
        prefilledAnswers: action.payload.data.prefilled || {},
        walletId: get(createUserExtras, 'user.walletId')
          ? createUserExtras.user.walletId
          : state.walletId,
        showPlanFlowReturningUserMessage: false
      };

    case ANSWER_FAILURE:
      if (action.error.response && action.error.response.data) {
        answerFailureMessage = Array.isArray(action.error.response.data.message)
          ? action.error.response.data.message[0].message
          : action.error.response.data.message;
        trackRegistrationError({
          eventProps: {
            registration_step: 'registration',
            error_message: answerFailureMessage
          }
        });
      }
      return {
        ...state,
        loading: false,
        answerLoading: false,
        submitPlaidLoading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: answerFailureMessage,
        registeredEmailError:
          answerFailureMessage ===
          'The email address you entered is already associated with a Greenlight account. Please login with that account or contact support.',
        showPlanFlowReturningUserMessage: false
      };

    case COMPLETE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        message: '',
        userCompleted: true,
        registeredUser: get(action.payload.data, 'extras')
          ? { ...state.registeredUser, ...action.payload.data.extras[0] }
          : { ...state.registeredUser }
      };

    case COMPLETE_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'complete',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: getErrorMessage(action)
      };

    case RESOLVE_ERROR_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        message: '',
        unresolvedErrors: action.payload.data.unresolvedErrors || []
      };

    case RESOLVE_ERROR_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'widget_page',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: getErrorMessage(action)
      };

    case SAVE_DEBIT_SUCCESS:
      setFundingAccountExtras = getExtras(
        action,
        'set_instant_funding_account'
      );

      // if needsVerify is set, then delegate to that, else use the unresolved error array
      // set it to null so that we're not testing true vs false which are both
      // cases we care about.
      const needsVerifyExtra = setFundingAccountExtras
        ? setFundingAccountExtras.needsVerify
        : null;
      const unresolvedErrorsEmpty =
        !action.payload.data.unresolvedErrors ||
        !action.payload.data.unresolvedErrors.length;

      return {
        ...state,
        questions: action.payload.data.nextQuestions,
        completeUser: !!(
          action.payload.data.nextQuestions &&
          action.payload.data.nextQuestions.indexOf('complete') !== -1
        ),
        extras: action.payload.data.extras
          ? action.payload.data.extras[0]
          : null,
        setFundingAccountExtras,
        needsVerification:
          needsVerifyExtra === null ? unresolvedErrorsEmpty : needsVerifyExtra,
        funding:
          setFundingAccountExtras && setFundingAccountExtras.funding
            ? setFundingAccountExtras.funding
            : null,
        flowId: action.payload.data.flowId,
        creditWaitlistEnabled:
          state.creditWaitlistEnabled &&
          isCreditWaitlistEnabledForFlow(action.payload.data.flowId),
        groupId: action.payload.data.groupId,
        totalGroupsInFlow: action.payload.data.totalGroupsInFlow,
        loading: false,
        error:
          action.payload.data.unresolvedErrors &&
          action.payload.data.unresolvedErrors.length > 0,
        message: '',
        unresolvedErrors:
          action.payload.data.errors ||
          action.payload.data.unresolvedErrors ||
          [],
        showPlanFlowReturningUserMessage: false
      };

    case SAVE_DEBIT_FAILURE:
      if (action.error.response && action.error.response.data) {
        saveDebitFailureMessage = Array.isArray(
          action.error.response.data.message
        )
          ? action.error.response.data.message[0].message
          : action.error.response.data.message;
        trackRegistrationError({
          eventProps: {
            registration_step: 'set_funding_account',
            error_message: saveDebitFailureMessage
          }
        });
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: saveDebitFailureMessage
      };

    case SAVE_MANUAL_ACH_SUCCESS:
      setFundingAccountExtras = getExtras(action, 'set_manual_bank_account');
      const isUnresolvedErrorsEmpty =
        !action.payload.data.unresolvedErrors ||
        !action.payload.data.unresolvedErrors.length;

      return {
        ...state,
        questions: action.payload.data.nextQuestions,
        completeUser: !!(
          action.payload.data.nextQuestions &&
          action.payload.data.nextQuestions.indexOf('complete') !== -1
        ),
        extras: action.payload.data.extras
          ? action.payload.data.extras[0]
          : null,
        needsVerification:
          isUnresolvedErrorsEmpty &&
          get(action.payload.data.extras[0], 'needsVerification'),
        setFundingAccountExtras,
        funding:
          setFundingAccountExtras && setFundingAccountExtras.funding
            ? setFundingAccountExtras.funding
            : null,
        flowId: action.payload.data.flowId,
        creditWaitlistEnabled:
          state.creditWaitlistEnabled &&
          isCreditWaitlistEnabledForFlow(action.payload.data.flowId),
        groupId: action.payload.data.groupId,
        totalGroupsInFlow: action.payload.data.totalGroupsInFlow,
        loading: false,
        error:
          action.payload.data.unresolvedErrors &&
          action.payload.data.unresolvedErrors.length > 0,
        message: '',
        unresolvedErrors:
          action.payload.data.errors || action.payload.data.unresolvedErrors,
        showPlanFlowReturningUserMessage: false
      };

    case SAVE_MANUAL_ACH_FAILURE:
      if (action.error.response && action.error.response.data) {
        saveACHFailureMessage = Array.isArray(
          action.error.response.data.message
        )
          ? action.error.response.data.message[0].message
          : action.error.response.data.message;
        trackRegistrationError({
          eventProps: {
            registration_step: 'set_funding_account',
            error_message: saveACHFailureMessage
          }
        });
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: saveACHFailureMessage
      };

    case VERIFY_DEBIT_NOW_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        message: '',
        needsVerification: false
      };

    case VERIFY_DEBIT_NOW_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'verify_funding',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        errorCode: action.error.response ? action.error.response.data.code : '',
        message: getErrorMessage(action)
      };

    case GET_PLAID_TOKEN_LOADING:
    case GET_BRAINTREE_TOKEN_LOADING:
    case GET_FUNDING_ACCOUNTS_LOADING:
      return {
        ...state,
        loading: true
      };

    case GET_BRAINTREE_TOKEN_SUCCESS:
      return {
        ...state,
        braintreeAccessToken: action.payload.data.token,
        loading: false
      };

    case GET_PLAID_TOKEN_SUCCESS:
      return {
        ...state,
        plaidLinkToken: action.payload.data.plaidLinkToken,
        loading: false
      };

    case GET_FUNDING_ACCOUNTS_SUCCESS:
      return {
        ...state,
        fundingAccounts: action.payload.data.pendingVerification[0],
        loading: false,
        error: false
      };

    case GET_BRAINTREE_TOKEN_FAILURE:
    case GET_FUNDING_ACCOUNTS_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'set_funding_account',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getErrorMessage(action)
      };

    case GET_PLAID_TOKEN_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'set_funding_account',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getErrorMessage(action)
      };

    case VERIFY_DEBIT_LATER:
      return {
        ...state,
        message: '',
        error: false,
        needsVerification: true,
        skippedVerification: true
      };

    case GET_ANSWERS_LOADING:
      return {
        ...state,
        loading: true
      };

    case GET_ANSWERS_SUCCESS:
      return {
        ...state,
        partner: get(action, 'payload.data.setPartnerSource') || state.partner,
        promotionId:
          get(action, 'payload.data.setReferralCode') || state.promotionId,
        answers: action.payload.data,
        loading: false
      };

    case GET_ANSWERS_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'widget_page',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getErrorMessage(action)
      };
    case CREATE_ERROR:
      return {
        ...state,
        error: action.payload.error,
        message: action.payload.message,
        timeoutError:
          action.payload.message ===
          'Your session has expired. Please log in again.'
            ? action.payload.message
            : '',
        showPlanFlowReturningUserMessage: false
      };

    case CLEAR_ERROR:
      return { ...state, error: null, message: null };

    case START_LOADING:
      return {
        ...state,
        loading: true,
        submitPlaidLoading: action.payload.isPlaid || false
      };

    case STOP_LOADING:
      return { ...state, loading: false };

    case SET_HIDE_FUNDING:
      return {
        ...state,
        hideFunding: {
          hidePlaid: action.payload.hidePlaid,
          hideManualACH: action.payload.hideManualACH,
          hideDebit: action.payload.hideDebit
        }
      };

    case SET_DEVICE_UUID:
      return { ...state, deviceToken: action.payload.deviceUUID };

    case ACCESS_REQUEST_LOADING:
    case CONFIRM_CODE_LOADING:
    case LOGIN_LOADING:
    case GET_USER_LOADING:
    case QUERY_GET_USER_LOADING:
    case QUERY_GET_INITIAL_FUNDING_LOADING:
    case GET_PRICING_PLAN_LOADING:
    case VOICE_PIN_LOADING:
      return { ...state };

    case GET_USER_SUCCESS:
      nextRegStep = action.payload.data.user.nextRegisterStep;
      if (
        action.payload.data.user.nextRegisterStep === 'complete' &&
        action.payload.data.user.role === 'child'
      ) {
        nextRegStep = 'complete_child';
      }
      return {
        ...state,
        loading: false,
        error: false,
        message: null,
        nextRegStep,
        tempPhoneNumber: action.payload.data.user.phoneNumber,
        registeredUser: {
          ...action.payload.data,
          token: action.payload.config.headers.Authorization
        },
        jwt: action.payload.data.token,
        preregisteredUser: action.payload.data.preregisteredUser,
        role: action.payload.data.preregisteredUser
          ? action.payload.data.preregisteredUser.role
          : state.role,
        isParentInvitedByChild:
          action.payload.data.preregisteredUser &&
          action.payload.data.preregisteredUser.role === 'parent',
        isChildInvitedByParent:
          action.payload.data.preregisteredUser &&
          action.payload.data.preregisteredUser.role === 'child'
      };

    case QUERY_GET_USER_SUCCESS:
      nextRegStep = action.payload.data.user.nextRegisterStep;

      return {
        ...state,
        loading: false,
        error: false,
        message: null,
        nextRegStep,
        tempPhoneNumber: action.payload.data.user.phoneNumber,
        registeredUser: {
          ...action.payload.data,
          token: action.payload.config.headers.Authorization
        },
        jwt: action.payload.data.token
      };

    case QUERY_GET_INITIAL_FUNDING_SUCCESS:
      return {
        ...state,
        loading: false,
        achError: !!action.payload.data.achDisabledMessage,
        achMessage: action.payload.data.achDisabledMessage,
        initialLoadAmount: action.payload.data.amount
      };

    case ACCESS_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        regAccessToken: action.payload.data.accessToken,
        nextRegStep: action.payload.data.action
      };

    case CONFIRM_CODE_SUCCESS:
      return {
        ...state,
        loading: false,
        error: false,
        regAccessToken: action.payload.data.accessToken,
        preregisteredUser: action.payload.data.preregisteredUser,
        role: action.payload.data.preregisteredUser
          ? action.payload.data.preregisteredUser.role
          : state.role,
        nextRegStep: action.payload.data.preregisteredUser
          ? action.payload.data.preregisteredUser.role !== 'child'
            ? action.payload.data.action
            : action.payload.data.preregisteredUser.nextRegisterStep // If a second approver, do the register thing. Didn't want to break invited-child flow
          : action.payload.data.action,
        isParentInvitedByChild:
          action.payload.data.preregisteredUser &&
          action.payload.data.preregisteredUser.role === 'parent',
        isChildInvitedByParent:
          action.payload.data.preregisteredUser &&
          action.payload.data.preregisteredUser.role === 'child',
        needsAccessRequest: false
      };

    case CHECK_PHONE_LOADING:
      return {
        ...state,
        loading: true
      };

    case CHECK_PHONE_SUCCESS:
      return checkPhoneSuccess(action, state);

    case CHECK_PHONE_FAILURE:
      const checkPhoneErrorMessage =
        get(action, 'error.response.data.message') ||
        ERROR_MESSAGES.defaultCheckPhoneFailure;
      trackRegistrationError({
        eventProps: {
          registration_step: 'phone_number',
          error_message: checkPhoneErrorMessage
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: checkPhoneErrorMessage
      };

    case VOICE_PIN_SUCCESS:
      return { ...state, loading: false };

    case VOICE_PIN_FAILURE:
      const voicePinErrorMessage = action.error.response
        ? action.error.response.data.message
        : '';
      trackRegistrationError({
        eventProps: {
          registration_step: 'confirm_code',
          error_message: voicePinErrorMessage
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: voicePinErrorMessage
      };

    case CONFIRM_CODE_FAILURE:
      const confirmCodeErrorMessage = action.error.response
        ? action.error.response.data.message
        : '';
      trackRegistrationError({
        eventProps: {
          registration_step: 'confirm_code',
          error_message: confirmCodeErrorMessage
        }
      });
      return {
        ...state,
        error: true,
        message: confirmCodeErrorMessage,
        loading: false
      };

    case ACCESS_REQUEST_FAILURE:
      const accessRequestErrorMessage = action.error.response
        ? action.error.response.data.message
        : '';
      trackRegistrationError({
        eventProps: {
          registration_step: 'phone_number',
          error_message: accessRequestErrorMessage
        }
      });
      return {
        ...state,
        nextRegStep: false,
        error: true,
        message: accessRequestErrorMessage,
        loading: false
      };

    case CHECK_VALID_EMAIL_SUCCESS:
      return {
        ...state,
        emailCorrection: action.payload.data.corrected || null
      };

    case FORGOT_PASSWORD_LOADING:
      return { ...state, loading: true, error: null };

    case FORGOT_PASSWORD_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        message: action.payload.data.message
      };

    case FORGOT_PASSWORD_FAILURE:
      const forgotPasswordErrorMessage = action.error.response
        ? action.error.response.data.message
        : '';
      trackRegistrationError({
        eventProps: {
          registration_step: 'forgot_password',
          error_message: forgotPasswordErrorMessage
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: forgotPasswordErrorMessage
      };

    case RESET_PASSWORD_LOADING:
      return { ...state, loading: true, error: null };

    case RESET_PASSWORD_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        message: action.payload.data.message,
        regAccessToken: action.payload.data.accessToken,
        nextRegStep: 'login',
        tempPhoneNumber: action.payload.data.user.phoneNumber
      };

    case RESET_PASSWORD_FAILURE:
      const resetPasswordErrorMessage = action.error.response
        ? action.error.response.data.message
        : '';
      trackRegistrationError({
        eventProps: {
          registration_step: 'reset_password',
          error_message: resetPasswordErrorMessage
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: resetPasswordErrorMessage
      };

    case LOGIN_SUCCESS:
      return {
        ...state,
        ...getLoginSuccessResponse(action, state)
      };

    case LOGIN_FAILURE:
      if (action.error.response) {
        const deactivatedErrorMsg = state.newDeactivatedErrorMsgEnabled
          ? ERROR_MESSAGES.loginFailureErrDeactivatedApp
          : ERROR_MESSAGES.loginFailureErrDeactivated;
        loginErrorMessage =
          action.error.response.data.code === 'ERR_DEACTIVATED'
            ? deactivatedErrorMsg
            : action.error.response.data.message;
        trackRegistrationError({
          eventProps: {
            registration_step: 'login',
            error_message: loginErrorMessage
          }
        });
      }

      return {
        ...state,
        loading: false,
        error: true,
        message: loginErrorMessage,
        registeredUser: null
      };

    case GET_USER_FAILURE:
    case QUERY_GET_USER_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'webreg',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getErrorMessage(action)
      };

    case QUERY_GET_INITIAL_FUNDING_FAILURE:
      trackRegistrationError({
        eventProps: {
          registration_step: 'set_funding_account',
          error_message: getErrorMessage(action)
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getErrorMessage(action)
      };

    case SET_NEXT_REG_STEP:
      return { ...state, nextRegStep: action.payload.nextRegStep };

    case SET_DOB:
      return setNextRegStepFromDob(action.payload.dob, state);

    case SET_ROLE:
      const { role } = action.payload;
      const { nextRegisterStep, needsAccessRequest } = setNextRegStepFromRole(
        role,
        state.needsAccessRequest,
        state.proveMobileAuthState
      );

      return {
        ...state,
        role,
        needsAccessRequest,
        nextRegStep: nextRegisterStep
      };

    case GET_BANK_NAME_LOADING:
      return { ...state, bankName: '' };

    case GET_BANK_NAME_SUCCESS:
      return { ...state, bankName: action.payload.data.name };

    case GET_BANK_NAME_FAILURE:
      return { ...state, bankName: null };

    case GET_MICRODEPOSITS_LOADING:
    case GET_MICRODEPOSITS_SUCCESS:
    case GET_MICRODEPOSITS_FAILURE:
      return { ...state };

    case SET_PREREGISTERED_USER:
      return { ...state, preregisteredUser: action.payload.preregisteredUser };

    case COMPLETE_RESET_KBA_FLOW:
      return { ...state, resetKBAFlow: false };

    case SET_MINIMUM_WALLET_LOAD_AMOUNT:
      return {
        ...state,
        walletMinimum: {
          shouldShift: action.payload.shouldShift || false,
          amount: action.payload.minimumLoadAmount || 20,
          displayAmount: action.payload.displayMinimumAmount
        }
      };

    case DISPLAY_LEGAL_ADDRESS:
      return {
        ...state,
        displayLegalAddress: action.payload.display
      };

    case SET_SMS_NOTIFICIATIONS_OPTED_STATUS:
      return {
        ...state,
        smsNotificationsOptedStatus: action.payload.optedStatus
      };

    case SET_PLAID_OAUTH_MANUAL_OVERRIDE:
      return {
        ...state,
        plaidOAuthManualOverrideEnabled: action.payload.enabled
      };

    case SET_PLAID_OAUTH_ANDROID_LINK:
      return {
        ...state,
        plaidOAuthAndroidLinkEnabled: action.payload.enabled
      };

    case SET_NATIVE_PLAID_FLOW_ENABLED:
      return {
        ...state,
        nativePlaidFlowEnabled: true
      };

    case SET_PHONE_NUMBER_PROVIDED:
      return {
        ...state,
        phoneNumberProvided: true
      };

    case SET_FIRST_NAME:
      return {
        ...state,
        firstName: action.payload.firstName
      };

    case SET_PARTNER:
      return {
        ...state,
        partner: action.payload.partner,
        registrationId: action.payload.registrationId
      };

    case SET_PROMOTION_ID:
      return {
        ...state,
        promotionId: action.payload.promotionId
      };

    case SET_PARTNER_PARAMS:
      return {
        ...state,
        partner: action.payload.partner,
        registrationId: action.payload.registrationId,
        phoneNumber: action.payload.phoneNumber,
        firstName: action.payload.firstName,
        promotionId: action.payload.promotionId
      };

    case SET_SHORTCUT:
      return {
        ...state,
        shortcut: action.payload.shortcut,
        shortcutPlan: action.payload.shortcutPlan
      };

    case GET_PRICING_PLAN_SUCCESS:
      return {
        ...state,
        currentPlan: get(action, 'payload.data.current'),
        loading: false
      };

    case GET_PRICING_PLAN_FAILURE:
      const getPricingPlanErrorMessage =
        get(action, 'error.response.data.message') ||
        ERROR_MESSAGES.defaultGetPricingPlanFailure;
      trackRegistrationError({
        eventProps: {
          registration_step: 'set_plan',
          error_message: getPricingPlanErrorMessage
        }
      });
      return {
        ...state,
        loading: false,
        error: true,
        message: getPricingPlanErrorMessage
      };

    case SET_TOGGLES_LOADED:
      return {
        ...state,
        togglesDirty: !action.payload.loaded
      };

    case SET_IS_FB_PIXEL_ENABLED:
      return {
        ...state,
        isFbPixelEnabled: action.payload.enabled
      };

    case INITIALIZE_IN_APP:
      inAppPreReg =
        action.payload.preregisteredUserId && !action.payload.authToken;

      return {
        ...state,
        regAccessToken: action.payload.accessToken,
        tempPhoneNumber: action.payload.phoneNumber,
        role: action.payload.role,
        registeredUser: {
          token: action.payload.authToken,
          id: action.payload.preregisteredUserId
        },
        stargatewayKey: action.payload.userToken,
        appOs: action.payload.appOs,
        appVersion: action.payload.appVersion,
        embeddedWebreg: true,
        nextRegStep: 'register',
        inAppPreReg,
        isParentInvitedByChild: inAppPreReg && action.payload.role === 'parent',
        isChildInvitedByParent: inAppPreReg && action.payload.role === 'child'
      };

    case SET_NO_PHONE_REG_ENTRY:
      return {
        ...state,
        noPhoneRegEntry: true
      };

    case SET_LINK_SOURCE:
      return {
        ...state,
        linkSource: action.payload.linkSource
      };

    case SET_EMAIL_BLUR_ENABLED:
      return {
        ...state,
        emailBlurEnabled: true
      };
    case SET_IP_ADDRESS:
      return {
        ...state,
        ipAddress: action.payload.ipAddress
      };
    case SET_ACCESS_MANAGEMENT_ENABLED:
      return {
        ...state,
        accessManagementEnabled: action.payload.accessManagementEnabled
      };
    case SET_PROMO_CODE_VERIFICATION_ENABLED:
      return {
        ...state,
        promoCodeVerificationEnabled: action.payload.enabled
      };
    case SET_PLAN_CHANGES_TERMS_AND_CONDITIONS:
      return {
        ...state,
        planChangesTermsAndConditions: true
      };
    case SET_PROVE_MOBILE_AUTH_ENABLED:
      return {
        ...state,
        proveMobileAuthEnabled: true,
        noPhoneNumberPriceText: action.payload.noPhoneNumberPriceText
      };
    case GET_PROVE_REDIRECT_URI_LOADING:
      return {
        ...state,
        proveMobileAuthState: 'initial-redirect'
      };
    case GET_PROVE_REDIRECT_URI_SUCCESS:
      return {
        ...state,
        proveMobileAuthState: 'pending',
        loading: false,
        proveRedirectUri: action.payload.data.redirect.destination
      };
    case GET_PROVE_REDIRECT_URI_FAILURE:
      return {
        ...state,
        proveMobileAuthState: 'failure',
        loading: false
      };
    case SET_CREDIT_WAITLIST:
      return {
        ...state,
        creditWaitlistEnabled: action.payload.enabled,
        creditWaitlistShowConfirmScreen: action.payload.showConfirmScreen
      };
    case SET_SMS_NOTIFICATIONS_ENABLED:
      return {
        ...state,
        smsNotificationsEnabled: true,
        smsNotificationsDefaultChecked: action.payload.defaultChecked
      };

    case SET_PREFER_NOT_TO_SAY_OPTION_ENABLED:
      return {
        ...state,
        preferNotToSayOptionEnabled: true
      };
    case SET_APPROVER_INVITE_PROPERTIES:
      return {
        ...state,
        tempPhoneNumber: action.payload.phoneNumber,
        placeholderToken: action.payload.placeholderToken
      };
    case SET_REQUIRE_TOKEN_FOR_APPROVERS_ENABLED:
      return {
        ...state,
        requireTokenForApproversEnabled: true
      };
    case VALIDATE_PLACEHOLDER_TOKEN_SUCCESS:
      return {
        ...state,
        shouldBounceInvitedUsersWhoDidntUseInviteLink: action.payload.data.user
          ? action.payload.data.user.id !== state.preregisteredUser.id
          : true,
        loading: false
      };
    case VALIDATE_PLACEHOLDER_TOKEN_FAILURE:
      return {
        ...state,
        shouldBounceInvitedUsersWhoDidntUseInviteLink: true,
        loading: false
      };
    case SET_PMA_FINAL_TARGET:
      return {
        ...state,
        tempPhoneNumber: action.payload.phoneNumber,
        deviceToken: action.payload.deviceUuid,
        regAccessToken: action.payload.regAccessToken,
        nextRegStep: action.payload.nextRegStep,
        proveMobileAuthState: 'success',
        needsAccessRequest: false
      };
    case SET_PMA_STATE:
      return {
        ...state,
        proveMobileAuthState: action.payload.state
      };
    case SET_MOCK_PMA:
      return {
        ...state,
        mockPma: {
          enabled: true,
          state: action.payload.state,
          phone: action.payload.phone
        }
      };
    case EMAIL_VERIFICATION_SUCCESS:
      return {
        ...state,
        emailVerificationStatus: 'verified'
      };
    case EMAIL_VERIFICATION_FAILURE:
      return {
        ...state,
        emailVerificationStatus: 'unverified'
      };
    case INVALIDATE_PMA:
      return {
        ...state,
        proveMobileAuthState: 'invalidated',
        regAccessToken: null,
        needsAccessRequest: true
      };
    case SET_ENHANCED_DEBIT_OPTIONS:
      return {
        ...state,
        enhancedDebit: {
          enhancedDebitEnabled: true,
          applePayEnabled: action.payload.applePayEnabled,
          googlePayEnabled: action.payload.googlePayEnabled,
          threeDSEnabled: action.payload.threeDSEnabled,
          paypalEnabled: action.payload.paypalEnabled,
          venmoEnabled: action.payload.venmoEnabled
        }
      };
    case SET_FEATURE_TOGGLES:
      return {
        ...state,
        featureToggles: action.payload.featureToggles
      };
    case SET_NEW_DEACTIVATED_ERROR_MSG_ENABLED:
      return {
        ...state,
        newDeactivatedErrorMsgEnabled: true
      };
    case SET_LAYOUT_TYPE:
      return {
        ...state,
        layoutType: action.payload.layoutType
      };
  }
  return state;
}
